<template>
  <nav
    class="mypage-subnav-wrapper card border-0 shadow-sm p-3"
    aria-label="My Page"
  >
    <h5 class="h6 mb-3">마이 페이지</h5>

    <ul class="nav nav-pills flex-column gap-1">
      <li v-for="menu in MENUS" :key="menu.name" class="nav-item">
        <button
          type="button"
          class="nav-link d-flex justify-content-between align-items-center text-start"
          :class="{ active: isActiveMenu(menu.name) }"
          :aria-current="isActiveMenu(menu.name) ? 'page' : null"
          @click="handleNavigate(menu.name)"
        >
          <span class="text-truncate">{{ menu.label }}</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<script setup>
/* ===========================================================================
 * 1) Imports
 * =========================================================================== */
import { ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { logError } from "@/utils/logHelper";

/* ===========================================================================
 * 2) 상수 정의
 * =========================================================================== */
const MENUS = Object.freeze([
  { name: "Order History", label: "주문내역", path: "/mypage/order" },
  { name: "My Model", label: "내 모델 관리", path: "/mypage/model" },
  { name: "My Alarm", label: "알림 내역", path: "/mypage/alarm" },
  { name: "Idea Quote", label: "견적 문의", path: "/mypage/idealist" },
  { name: "Coupon", label: "쿠폰함", path: "/mypage/coupon" },
  { name: "Address", label: "배송지 관리", path: "/mypage/address" },
  { name: "My Account", label: "계정 관리", path: "/mypage/account" },
]);

// name → path 매핑 (O(1) 조회)
const MENU_PATH_MAP = Object.freeze(
  MENUS.reduce((acc, menu) => {
    acc[menu.name] = menu.path;
    return acc;
  }, {})
);

// path prefix → menu 매핑 (긴 경로부터 정렬하여 구체적인 경로 먼저 매칭)
const SORTED_MENUS_BY_PATH = Object.freeze(
  [...MENUS].sort((a, b) => b.path.length - a.path.length)
);

/* ===========================================================================
 * 3) 컴포넌트 옵션
 * =========================================================================== */
defineOptions({
  name: "MyPageSubNav",
});

/* ===========================================================================
 * 4) Composables
 * =========================================================================== */
const route = useRoute();
const router = useRouter();

/* ===========================================================================
 * 5) 상태 변수
 * =========================================================================== */
const activeMenu = ref("");

/* ===========================================================================
 * 6) 유틸리티 함수
 * =========================================================================== */
/**
 * 현재 경로에 맞는 활성 메뉴 찾기
 */
function findActiveMenuByPath(currentPath) {
  if (typeof currentPath !== "string") return "";

  // 정렬된 배열로 가장 구체적인 경로 먼저 매칭
  const matchedMenu = SORTED_MENUS_BY_PATH.find((menu) =>
    currentPath.startsWith(menu.path)
  );

  return matchedMenu?.name || "";
}

/**
 * 활성 메뉴 상태 업데이트
 */
function updateActiveMenu(currentPath) {
  // meta 정보 우선 확인
  if (route.meta?.activeMenu) {
    activeMenu.value = route.meta.activeMenu;
    return;
  }

  // 경로 매칭
  activeMenu.value = findActiveMenuByPath(currentPath);
}

/**
 * 메뉴 활성 상태 확인
 */
function isActiveMenu(menuName) {
  return activeMenu.value === menuName;
}

/* ===========================================================================
 * 7) 이벤트 핸들러
 * =========================================================================== */
function handleNavigate(menuName) {
  const path = MENU_PATH_MAP[menuName];

  if (!path) return;

  // 현재 경로와 같으면 네비게이션 스킵
  if (route.path === path) return;

  try {
    router.push(path);
  } catch (error) {
    logError("handleNavigate() 에서 오류 발생", error);
  }
}

/* ===========================================================================
 * 8) Watch
 * =========================================================================== */
watch(
  () => route.path,
  (newPath) => {
    updateActiveMenu(newPath);
  },
  { immediate: true }
);
</script>
